/**
 * Attach an MP4 only when the video is near the viewport.
 * Honour prefers-reduced-motion and Save-Data — decorative autoplay must not
 * compete with LCP or burn metered bandwidth.
 */
export function useLazyVideo(src: string, rootMargin = '200px') {
  const videoRef = ref<HTMLVideoElement | null>(null)
  let observer: IntersectionObserver | null = null

  onMounted(() => {
    const el = videoRef.value
    if (!el) return

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const saveData = Boolean(
      (navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData,
    )
    // Poster stays visible; no network request for the MP4.
    if (reduceMotion || saveData) return

    let loaded = false
    const load = () => {
      if (loaded) return
      loaded = true
      // Prefer <source> + load() over setting el.src so preload="none" stays
      // honest until we explicitly ask the browser to fetch.
      const source = document.createElement('source')
      source.src = src
      source.type = 'video/mp4'
      el.append(source)
      el.load()
      void el.play().catch(() => { /* autoplay refused; poster stands in */ })
    }

    if (!('IntersectionObserver' in window)) {
      load()
      return
    }

    observer = new IntersectionObserver((entries) => {
      if (!entries.some(entry => entry.isIntersecting)) return
      load()
      observer?.disconnect()
      observer = null
    }, { rootMargin })

    observer.observe(el)
  })

  onBeforeUnmount(() => {
    observer?.disconnect()
    observer = null
  })

  return videoRef
}
